import React, { useState, useEffect, useRef } from "react";
import { useAppSelector } from "../../redux/hooks";
import { checkCurrentCheckoutDelivery } from "../../services/products/checkout-delivery.service";
import "./sidecart.scss";

import SidecartItems from "./sidecart-items";
import SidecartAddress from "./sidecart-address";
import SidecartCourier from "./sidecart-courier";
import SidecartPayment from "./sidecart-payment";
import SidecartSummary from "./sidecart-summary";

export type SidecartView = "CART" | "ADDRESS" | "COURIER" | "PAYMENT" | "ORDER SUMMARY";

interface SideCartFlowProps {
    isOpen: boolean;
    onClose: () => void;
}

const SideCartFlow: React.FC<SideCartFlowProps> = ({ isOpen, onClose }) => {
    const [currentView, setCurrentView] = useState<SidecartView>("CART");
    const [isClosing, setIsClosing] = useState(false);
    const [checkingStock, setCheckingStock] = useState(false);
    const [stockError, setStockError] = useState<string | null>(null);
    const addressId = useAppSelector(s => s.userAddress.selecedUserAddresses?.userAddressID);
    const customerId = useAppSelector(s => s.adminUser.selectedAdminUserId) || undefined;
    const current = useRef({ currentView, addressId, customerId, isOpen });
    if (current.current.currentView !== currentView || current.current.addressId !== addressId ||
        current.current.customerId !== customerId || current.current.isOpen !== isOpen)
        current.current = { currentView, addressId, customerId, isOpen };
    const inFlight = useRef(false), mounted = useRef(true);
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
    useEffect(() => { setStockError(null); }, [currentView, addressId, customerId, isOpen]);

    const advanceAfterStockCheck = async (next: SidecartView) => {
        if (inFlight.current) return;
        const before = current.current;
        inFlight.current = true;
        setCheckingStock(true);
        setStockError(null);
        try {
            await checkCurrentCheckoutDelivery(before.addressId || 0, before.customerId);
            const now = current.current;
            if (mounted.current && now === before && now.isOpen && now.currentView === before.currentView &&
                now.addressId === before.addressId && now.customerId === before.customerId) setCurrentView(next);
        } catch (error) {
            if (mounted.current && current.current === before)
                setStockError(error instanceof Error ? error.message : 'Delivery stock could not be checked. Please retry.');
        } finally {
            inFlight.current = false;
            if (mounted.current) setCheckingStock(false);
        }
    };

    // Only manage body overflow and animation — do NOT reset step on open
    useEffect(() => {
        if (isOpen) {
            setIsClosing(false);
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }
    }, [isOpen]);

    // Call this after a successful order to reset the flow back to CART
    const resetToCart = () => {
        setCurrentView("CART");
    };

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            onClose();
            setIsClosing(false);
        }, 300); // Matches transition duration
    };

    const handleCloseAfterOrder = () => {
        resetToCart();
        handleClose();
    };

    const renderStep = () => {
        const stepClass = "animate-in";
        switch (currentView) {
            case "CART":
                return (
                    <div className={stepClass} key="cart">
                        <SidecartItems
                            mode="SIDECART"
                            onProceed={() => setCurrentView("ADDRESS")}
                            onClose={handleClose}
                        />
                    </div>
                );

            case "ADDRESS":
                return (
                    <div className={stepClass} key="address">
                        <SidecartAddress
                            onBack={() => setCurrentView("CART")}
                            onNext={() => setCurrentView("COURIER")}
                            onClose={handleClose}
                        />
                    </div>
                );

            case "COURIER":
                return (
                    <div className={stepClass} key="courier">
                        <SidecartCourier
                            mode="SIDECART"
                            onBack={() => setCurrentView("ADDRESS")}
                            onNext={() => { void advanceAfterStockCheck("ORDER SUMMARY"); }}
                            onClose={handleClose}
                        />
                    </div>
                );

            case "ORDER SUMMARY":
                return (
                    <div className={stepClass} key="summary">
                        <SidecartSummary
                            onBack={() => setCurrentView("COURIER")}
                            onNext={() => { void advanceAfterStockCheck("PAYMENT"); }}
                            onClose={handleClose}
                            onOrderComplete={handleCloseAfterOrder}
                            onChangeAddress={() => setCurrentView("ADDRESS")}
                            onChangeDeliveryMethod={() => setCurrentView("COURIER")}
                            onChangePaymentMethod={() => setCurrentView("PAYMENT")}
                        />
                    </div>
                );

            case "PAYMENT":
                return (
                    <div className={stepClass} key="payment">
                        <SidecartPayment
                            onBack={() => setCurrentView("ORDER SUMMARY")}
                            onNext={() => {}} // Final step now, placement happens inside
                            onChangeAddress={() => setCurrentView("ADDRESS")}
                            onClose={handleClose}
                            onOrderComplete={handleCloseAfterOrder}
                        />
                    </div>
                );

            default:
                return null;
        }
    };

    if (!isOpen && !isClosing) return null;

    return (
        <>
            {/* Overlay */}
            <div 
                className={`side-cart-overlay ${isOpen && !isClosing ? "open" : ""}`} 
                onClick={handleClose} 
            />

            {/* Panel */}
            <div className={`side-cart-panel ${isOpen && !isClosing ? "open" : ""}`}>
                {checkingStock && <p role="status" className="p-3 m-0">Checking delivery stock…</p>}
                {stockError && <div role="alert" className="p-error p-3">{stockError}</div>}
                {renderStep()}
            </div>
        </>
    );
};

export default SideCartFlow;
